/**
 * 通知の既読状態。
 *
 * 通知はサーバに実体が無く（承認待ちタスクと自分の案件から導出する）、既読の API も
 * 無いので端末ごとに localStorage で持つ。通知テーブルを新設したらサーバへ移す。
 *
 * 既読は 2 段構え。
 * - `lastSeenAt` … 「すべて既読にする」。この時刻より前の出来事はすべて既読
 * - `readAt`     … 個別に既読にした通知。**id → その通知が指していた出来事の発生時刻**
 *
 * `readAt` に「クリックした時刻」ではなく**出来事の発生時刻**を入れるのが要点。
 * 通知 id は `case:<caseId>:<status>` のように使い回されるため（同じ案件が再度
 * 差し戻されると id は同じで発生時刻だけ新しくなる）、発生時刻を基準にすれば
 * 「再発したものは再び未読」が自然に成り立つ。クリック時刻を保存すると、サーバの
 * 時計が進んでいる場合に「押しても未読のまま」になり得る。
 *
 * ベル（ヘッダ）と通知一覧は別々に `useNotifications()` を呼ぶので、既読状態は
 * React の state ではなく外部ストアに置き、`useSyncExternalStore` で両方が購読する。
 */
export const LAST_SEEN_STORAGE_KEY = "zeus-bpm:notifications:lastSeenAt";
export const READ_AT_STORAGE_KEY = "zeus-bpm:notifications:readAt";

/** 個別既読の保持上限。通知の供給源（承認 100 + 案件 50）より十分大きく取る。 */
export const READ_AT_MAX_ENTRIES = 300;

export type NotificationReadState = {
  readonly lastSeenAt: Date | null;
  /** 通知 id → 既読にした出来事の発生時刻（epoch ミリ秒）。 */
  readonly readAt: ReadonlyMap<string, number>;
};

/**
 * 現在の状態。`useSyncExternalStore` は**同一参照**を返すことを要求するので、
 * 書き込みのときだけ作り替える（毎回作ると無限再レンダになる）。
 */
let snapshot: NotificationReadState | null = null;
const listeners = new Set<() => void>();

/** プライベートブラウジング等で localStorage に触れない環境でも動作を続ける。 */
function readRaw(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeRaw(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // 保存できなくてもメモリ上の状態で動く（タブを閉じるまでは既読が効く）
  }
}

function parseLastSeenAt(raw: string | null): Date | null {
  if (raw === null) return null;

  const at = new Date(raw);
  return Number.isNaN(at.getTime()) ? null : at;
}

/**
 * id → 時刻の map なので、このファイルだけ JSON を使う
 * （他の保存箇所は単一の値なので素の文字列）。壊れた値は捨てる。
 */
function parseReadAt(raw: string | null): Map<string, number> {
  if (raw === null) return new Map();

  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      return new Map();
    }

    return new Map(
      Object.entries(parsed as Record<string, unknown>).filter(
        (entry): entry is [string, number] =>
          typeof entry[1] === "number" && Number.isFinite(entry[1]),
      ),
    );
  } catch {
    return new Map();
  }
}

function load(): NotificationReadState {
  return {
    lastSeenAt: parseLastSeenAt(readRaw(LAST_SEEN_STORAGE_KEY)),
    readAt: parseReadAt(readRaw(READ_AT_STORAGE_KEY)),
  };
}

export function getReadState(): NotificationReadState {
  snapshot ??= load();
  return snapshot;
}

export function subscribeReadState(listener: () => void): () => void {
  listeners.add(listener);

  // 他のタブで既読にした場合も追随する（key が null は clear()）
  const onStorage = (event: StorageEvent) => {
    if (
      event.key === null ||
      event.key === LAST_SEEN_STORAGE_KEY ||
      event.key === READ_AT_STORAGE_KEY
    ) {
      snapshot = load();
      emit();
    }
  };
  window.addEventListener("storage", onStorage);

  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
}

function emit(): void {
  for (const listener of listeners) listener();
}

/**
 * 個別既読の枝刈り。
 *
 * - `lastSeenAt` 以前の印は落とす（時刻側で既読と判定されるので冗長）
 * - 上限を超えたら発生時刻の新しい順に残す
 *
 * 「古い印を年齢で落とす」ことはしない。ここに入っているのは**出来事の発生時刻**なので、
 * 何年も前に完了した案件の通知（一覧にはまだ出る）を既読にできなくなってしまう。
 */
function pruneReadAt(
  readAt: ReadonlyMap<string, number>,
  lastSeenAt: Date | null,
): Map<string, number> {
  const seen = lastSeenAt?.getTime() ?? Number.NEGATIVE_INFINITY;

  const kept = [...readAt.entries()]
    .filter(([, at]) => at > seen)
    .sort((a, b) => b[1] - a[1])
    .slice(0, READ_AT_MAX_ENTRIES);

  return new Map(kept);
}

function commit(next: NotificationReadState): void {
  snapshot = next;

  if (next.lastSeenAt !== null) {
    writeRaw(LAST_SEEN_STORAGE_KEY, next.lastSeenAt.toISOString());
  }
  writeRaw(
    READ_AT_STORAGE_KEY,
    JSON.stringify(Object.fromEntries(next.readAt)),
  );

  emit();
}

/**
 * 1 件を既読にする。`occurredAtMillis` はその通知が指す出来事の発生時刻
 * （発生時刻が無い通知は呼び出し側が現在時刻を渡す）。
 */
export function markRead(id: string, occurredAtMillis: number): void {
  const current = getReadState();
  const next = new Map(current.readAt);
  next.set(id, occurredAtMillis);

  commit({
    lastSeenAt: current.lastSeenAt,
    readAt: pruneReadAt(next, current.lastSeenAt),
  });
}

/** すべて既読にする。個別の印は冗長になるので枝刈りで落ちる。 */
export function markAllRead(now: Date = new Date()): void {
  const current = getReadState();

  commit({
    lastSeenAt: now,
    readAt: pruneReadAt(current.readAt, now),
  });
}

/** テスト用。モジュールに持っている snapshot を捨てて読み直させる。 */
export function resetReadState(): void {
  snapshot = null;
}
